import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from "@angular/core";
import {Store} from "@ngrx/store";
import {TranslateService} from '@ngx-translate/core';
import {FontAwesomeService} from "app/services/font-awesome.service";
import {ACTIVE_SEARCH_SET, SEARCH_RESULT_REMOVE} from "app/store/actions/index";
import {AppState, selectShipments} from "app/store/reducers/index";
import {ConsignmentSections} from "app/model/consignment-section.enum";
import {Shipment} from "app/model/shipment.interface";
import {first, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs/Subject";
import {SearchResult} from "@store/reducers";
import {ShipmentService} from "@services/shipment.service";
import {Product} from "@models/product.interface";
import {ShipmentAdd, ShipmentRemove} from "@store/actions/order-creation.action";
import {PackageDetailsService} from "@services/package-details.service";
import {ShipmentForOrder} from "@models/shipment.interface";
import {Router} from "@angular/router";
import {CurrencyFormatPipe} from '@pipes/currency-fomat.pipe';
import {DateFormatPipe} from '@pipes/date-format.pipe';

@Component({
  selector: 'app-search-result',
  template: `
  <div class="search-result-container">
    <app-search-summary
      [failure]="!searchResult.loaded && !searchResult.loading"
      [data]="shipmentSummaryData(searchResult.params)"
      [index]="index"
      (reloadSearchResult)="handleReload()"
      (editSearchResult)="handleEdit()"
      (deleteSearchResult)="handleDelete()"></app-search-summary>
    <ng-container *ngIf="!failure; else failureBlock">
      <app-search-result-card
        *ngFor="let product of displayedData; index as i;"
        [searchParams]="searchResult.params"
        [isActive]="getActiveStatus(product.product_id)"
        [product]="product"
        [isLoading]="searchResult.loading"
        [searchResultId]="searchResult.id"
        [fullHeight]="true"
        (toggleActive)="handleToggleActive($event, product, searchResult.params, searchResult.search_id)"
        [cheapest]="i === 0"
        [fastest]="i === 1"></app-search-result-card>
    </ng-container>
    <ng-template #failureBlock>
      <app-search-result-card-failure></app-search-result-card-failure>
    </ng-template>

  </div>
  <span *ngIf="searchResult?.products?.length > 2" (click)="toggleOpened()" class="toggle-items">
    <ng-container *ngIf="!opened; else openedContainer">
      <fa-icon [icon]="faService.faArrowCircleDown"></fa-icon>
        <span translate [translateParams]="{number:searchResult?.products?.length - 2}">account.section.createConsignment.searchResult.buttonShowMore</span>
    </ng-container>
    <ng-template #openedContainer>
      <fa-icon [icon]="faService.faArrowCircleUp"></fa-icon>
      <span translate>account.section.createConsignment.searchResult.buttonShowLess</span>
    </ng-template>
  </span>`,
  styleUrls: ['./search-result.component.scss']
})

export class SearchResultComponent implements OnInit {
  opened: boolean = false;
  shipments: Shipment[] = [];
  private unsubscribe: Subject<void> = new Subject();

  @Input() searchResult: SearchResult;
  @Input() index: number;

  @Output() reloadSearchRequest: EventEmitter<any> = new EventEmitter();

  constructor(
    public faService: FontAwesomeService,
    private store: Store<AppState>,
    private shipmentService: ShipmentService,
    private packagesDetailsService: PackageDetailsService,
    private router: Router,
    public translate: TranslateService
  ) {}

  ngOnInit() {
    this.store.select(selectShipments)
      .pipe(takeUntil(this.unsubscribe), first())
      .subscribe(shipments => {
        this.shipments = shipments;
        const isSelected = this.shipments.find(shipment => shipment && shipment.id.includes(this.searchResult.id));
        if (!isSelected && this.searchResult.products.length) {
          // console.log('searchResult.products[0]', this.searchResult.products[0]);
          this.store.dispatch(new ShipmentAdd({
            shipment: this.generateShipment(
              this.searchResult.products[0],
              this.buildUniqueId(
                this.searchResult.id,
                this.searchResult.products[0].product_id
              ),
              this.searchResult.params,
               this.searchResult.search_id
            )
          }));
        }
      });
    this.store.select(selectShipments)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(shipments => this.shipments = shipments);
  }

  get displayedData() {
    if (this.searchResult.loading) {
      return [0, 1];
    } else if (!this.searchResult.products.length) {
      return [0];
    } else if (this.opened) {
      return this.searchResult.products;
    } else {
      return this.searchResult.products.slice(0, 2);
    }
  }

  toggleOpened() {
    this.opened = !this.opened;
  }

  handleReload() {
    this.store.dispatch({
      type: ACTIVE_SEARCH_SET,
      payload: {
        activeSearchId: this.searchResult.id
      }
    });
    this.shipments.forEach(shipment => {
      if (shipment.id.indexOf(this.searchResult.id) + 1) {
        this.store.dispatch(new ShipmentRemove({id: shipment.id}));
      }
    });
    this.reloadSearchRequest.emit(this.searchResult);
  }

  handleEdit() {
    this.router.navigate(['create-consignment']);
    this.store.dispatch({
      type: ACTIVE_SEARCH_SET,
      payload: {
        activeSearchId: this.searchResult.id
      }
    });
    this.shipments.forEach(shipment => {
      if (shipment.id.indexOf(this.searchResult.id) + 1) {
        this.store.dispatch(new ShipmentRemove({id: shipment.id}));
      }
    });
  }

  handleDelete() {
    this.store.dispatch({
      type: SEARCH_RESULT_REMOVE,
      payload: {
        id: this.searchResult.id
      }
    });
    this.shipments.forEach(shipment => {
      if (shipment.id.indexOf(this.searchResult.id) + 1) {
        this.store.dispatch(new ShipmentRemove({id: shipment.id}));
      }
    });

    if (this.shipments.length === 0) {
      this.router.navigate(['shipments', 'saved']);
    }
  }

  handleToggleActive(id: string, product: Product, params: any, search_id) {
    if (!this.shipments.find(shipment => shipment.id === id)) {
      this.shipments.forEach((shipment) => {
        if (shipment.id.includes(this.searchResult.id)) {
          this.store.dispatch(new ShipmentRemove({id: shipment.id}));
        }
      });
      // console.log('handleToggleActive', id, product, params, search_id);
      const shipment = this.generateShipment(product, id, params, search_id);
      // console.log(shipment);
      this.store.dispatch(new ShipmentAdd({shipment}));
    }
  }

  generateShipment(product, id, params, search_id): Shipment | ShipmentForOrder {
    // console.log('Product', product);
    const from = params.from;
    const to = params.to;
    const packages = params.packages;
    const shipment: ShipmentForOrder = {
      from: {
        country: from.country,
        country_code: from.country_code,
        zip: from.zip,
        city: from.city,
        address: from.address,
        type: from.type,
        company_name: from.company_name ,
        // reg_number: from.reg_number,
        name: from.name,
        surname: from.surname,
        phone: from.phone,
        email: from.email,
        instructions: from.instructions,
        door_code: from.door_code
      },
      to: {
        country: to.country,
        country_code: to.country_code,
        zip: to.zip,
        city: to.city,
        address: to.address,
        type: to.type,
        company_name: to.company_name,
        // reg_number: to.reg_number,
        name: to.name,
        surname: to.surname,
        phone: to.phone,
        email: to.email,
        instructions: to.instructions,
        door_code: to.door_code,
        send_tracking_information: to.send_tracking_information
      },
      details: {
        reference: params.reference,
        product_id: product.product_id,
        delivery_type: product.delivery_type,
        currency: product.currency,
        charge: product.charge,
        vat: product.vat,
        parcel_value: params.parcel_value,
        parcel_content: params.parcel_content,
        daily_pickup: params.daily_pickup,
        pickup_date: product.pickup_date,
        pickup_time_from: product.pickup_time_from,
        pickup_time_to: product.pickup_time_to,
        delivery_date: product.delivery_date,
        delivery_time_from: product.delivery_time_from,
        delivery_time_to: product.delivery_time_to,
        latest_booking_time: product.latest_booking_time,
        // TODO: Calculate additional services
        // total_charge: product.charge + +product.addons.insurance.price,
        total_charge: product.charge,
        search_id,
        // for displaying
        time_until_delivery: product.time_until_delivery,
        price: product.charge,
        delivery_time: product.delivery_time,
        pickup_time: product.pickup_time,
        service_id: product.service_id,
        // addons: product.addons,
        additional_services: product.additional_services,
        referring_consignment_number: params.referring_consignment_number,
        // pickup_type: params.pickup_type,
        // delivery_type: params.delivery_type
      },
      packages,
      id,
      selected_additional_services: product.selected_additional_services,
      // addons: {
      //   insurance: {
      //     id: product.addons.insurance.info.id
      //   }
      // }
    };

    if (params.pickup) {
      shipment.pickup = {
        is_used: params.pickup.is_used,
        country: params.pickup.country,
        country_code: params.pickup.country_code,
        zip: params.pickup.zip,
        city: params.pickup.city,
        address: params.pickup.address,
        type: params.pickup.type,
        company_name: params.pickup.company_name,
        name: params.pickup.name,
        surname: params.pickup.surname,
        phone: params.pickup.phone,
        email: params.pickup.email,
        instructions: params.pickup.instructions,
        door_code: params.pickup.door_code
      };
    }

    if (params.delivery) {
      shipment.delivery = {
        is_used: params.delivery.is_used,
        country: params.delivery.country,
        country_code: params.delivery.country_code,
        zip: params.delivery.zip,
        city: params.delivery.city,
        address: params.delivery.address,
        type: params.delivery.type,
        company_name: params.delivery.company_name,
        name: params.delivery.name,
        surname: params.delivery.surname,
        phone: params.delivery.phone,
        email: params.delivery.email,
        instructions: params.delivery.instructions,
        door_code: params.delivery.door_code
      };
    }

    if (params.customs_data) {
      shipment.customs_data = {
        ...params.customs_data
      };
    }

    // console.log('generateShipment', shipment);

    return shipment;
  }

  getActiveStatus(product_id) {
    const id = this.buildUniqueId(this.searchResult.id, product_id);
    return this.shipments.find(shipment => shipment.id === id);
  }

  get failure() {
    return this.searchResult.loaded && !this.searchResult.products.length;
  }

  shipmentSummaryData(searchParams) {
    const packages = searchParams.packages;
    return {
      ...this.packagesDetailsService.getTotalPackagesData(packages, false),
      from: searchParams.from,
      to: searchParams.to,
    };
  }

  buildUniqueId(searchResultUuid: string, productId: string | number): string {
    return `${searchResultUuid}-${productId}`;
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/search-results/components/search-result/search-result.component.ts